import React, { useEffect, useRef, useState } from "react";
import { FieldInputProps, FormikProps } from "formik";
import { InputGroup } from "@blueprintjs/core";
import { PlanrPopover } from "modules/common/components/planr/popover/Popover";
import { apiUrls } from "modules/common/services/communication/urls";
import { useMst } from "modules/root/models/hooks";
import styles from "./BankSuggestInput.module.scss";

export type BankSuggestField = "bik" | "name" | "corr";

export interface KnownBank {
  bik: string;
  bankName: string;
  corrAccount: string;
}

/** Имена полей формы, которые заполняет выбор банка */
export interface BankSuggestTargets {
  bankIdentificator: string;
  bankName: string;
  corrAccount: string;
}

/** Сколько символов набрать, прежде чем спрашивать сервер */
const MIN_LENGTH: Record<BankSuggestField, number> = { bik: 1, name: 1, corr: 1 };
const DELAY = 300;

interface BankSuggestInputProps {
  suggest: BankSuggestField;
  field: FieldInputProps<string>;
  form: FormikProps<any>;
  targets: BankSuggestTargets;
  disabled?: boolean;
}

/**
 * Обычное поле реквизитов, которое при вводе показывает уже известные банки
 * (из других контрагентов, выписок, реквизитов компании). Клик по строке
 * заполняет сразу БИК, банк и корр. счёт.
 */
export const BankSuggestInput = ({ suggest, field, form, targets, disabled }: BankSuggestInputProps) => {
  const { transport, session } = useMst();
  const [items, setItems] = useState<KnownBank[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const focused = useRef(false);
  const request = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const close = () => {
    request.current++;
    clearTimeout(timer.current);
    setOpen(false);
    setActive(-1);
  };

  const search = (value: string) => {
    clearTimeout(timer.current);
    const query = value.trim();

    // В анкете агента без входа подсказка недоступна — не дёргаем сервер ради 401
    if (!session.loggedIn || query.length < MIN_LENGTH[suggest]) {
      close();
      return;
    }

    const current = ++request.current;
    timer.current = setTimeout(async () => {
      try {
        const data: KnownBank[] = await transport.get(apiUrls.bankDetails.banks(suggest, query));
        // Ответ на устаревший ввод или поле уже покинули
        if (current !== request.current || !focused.current) {
          return;
        }

        setItems(data);
        setActive(-1);
        setOpen(data.length > 0);
      } catch {
        // Подсказка необязательна: нет прав или сервер недоступен — просто молчим
        current === request.current && setOpen(false);
      }
    }, DELAY);
  };

  const select = (bank: KnownBank) => {
    const values: Array<[string, string]> = [
      [targets.bankIdentificator, bank.bik],
      [targets.bankName, bank.bankName],
      [targets.corrAccount, bank.corrAccount],
    ];

    values.forEach(([name, value]) => {
      form.setFieldValue(name, value);
      form.setFieldTouched(name, true, false);
    });

    close();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || !items.length) {
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (e.key === "Enter" && active >= 0) {
      // Не отправлять форму — Enter выбирает подсказку
      e.preventDefault();
      select(items[active]);
    } else if (e.key === "Escape") {
      e.stopPropagation();
      close();
    }
  };

  const content = (
    <div className={styles.list}>
      {items.map((bank, index) => (
        <div
          key={bank.bik}
          className={`${styles.item} ${index === active ? styles.active : ""}`}
          // mousedown, а не click: иначе поле теряет фокус и подсказка закрывается раньше выбора
          onMouseDown={(e) => {
            e.preventDefault();
            select(bank);
          }}
          onMouseEnter={() => setActive(index)}
        >
          <div className={styles.name}>{bank.bankName}</div>
          <div className={styles.details}>
            <span>
              БИК <Mark text={bank.bik} highlight={suggest === "bik"} />
            </span>
            {bank.corrAccount && (
              <span>
                к/с <Mark text={bank.corrAccount} highlight={suggest === "corr"} />
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <PlanrPopover
      isOpen={open && !disabled}
      content={content}
      placement="bottom-start"
      arrow={false}
      autoFocus={false}
      enforceFocus={false}
      fill={true}
      onInteraction={(next) => !next && close()}
      popoverClassName={styles.popover}
    >
      <InputGroup
        id={field.name}
        {...field}
        className="planr-default-input"
        autoComplete="off"
        disabled={disabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          field.onChange(e);
          search(e.target.value);
        }}
        onFocus={() => {
          focused.current = true;
        }}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          focused.current = false;
          close();
          field.onBlur(e);
        }}
        onKeyDown={onKeyDown}
      />
    </PlanrPopover>
  );
};

const Mark = ({ text, highlight }: { text: string; highlight: boolean }) =>
  highlight ? <b>{text}</b> : <>{text}</>;
